/**
 * Paste copied config button for ScaleDropModal.
 * Applies campaign, gateway, senders, and optionally texts from DropApplyStore.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { ClipboardPaste } from 'lucide-react';

import { Button } from '@/components/ui';
import { useDropApplyStore } from '@/stores/drop-apply.store';
import { useToast } from '@/providers/ToastProvider';

interface PasteConfigButtonProps {
  onPaste: (values: NonNullable<ReturnType<typeof useDropApplyStore.getState>['values']>) => void;
  disabled?: boolean;
}

function canPasteValues(
  v: NonNullable<ReturnType<typeof useDropApplyStore.getState>['values']>,
): boolean {
  return Boolean(
    v.gatewayName?.trim() ||
      (v.senderNames?.length ?? 0) > 0 ||
      (v.smsTexts?.length ?? 0) > 0,
  );
}

export const PasteConfigButton: FC<PasteConfigButtonProps> = ({ onPaste, disabled }) => {
  const values = useDropApplyStore((s) => s.values);
  const canPaste = values != null && canPasteValues(values);
  const { success: toastSuccess } = useToast();

  const handleClick = () => {
    if (!values || !canPaste) return;
    onPaste(values);
    toastSuccess('Config applied', 'Gateway, senders, and texts pasted.');
  };

  if (!canPaste) return null;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={disabled}
      className="gap-1.5"
      aria-label="Paste copied config (gateway, senders, texts)"
    >
      <ClipboardPaste className="h-3.5 w-3.5" />
      Paste
    </Button>
  );
};
